import { dates, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { SqlEditor } from "@reporting/rp-app-host";
import { ContextData, MultiContextData, ReportContext } from "@reporting/rp-types/analytics";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { AnalyticsApplication, Sidebar } from "..";
import { isMultiContext } from "../common";
import { openReport } from "./report";

const app = () => rp.applications.get<AnalyticsApplication>('analytics');

export const createName = (module: ModuleCode) => [
	module == 'D' ? 'dashboard' : (module == 'S' ? 'spreadsheet' : 'view'),
	dates.format(new Date(), 'yyyyMMdd-HHmmss')
].join('-');

export function getContextBroker() {
	return app().contextBroker;
}

export async function getContext(context: Partial<ReportContext>) {
	return await getContextBroker().getContext(context);
}

/**
 * Create a new named (empty) context for the given module and open it.
 * Prompts the user for the context name; returns the created context
 * or null when cancelled.
 */
export async function create(module: ModuleCode, defaults?: Partial<ContextData>) {
	const cmd = await rp.host.openMenu({
		mode: 'input',
		value: createName(module),
		text: msg(`Create new ${module == 'D' ? 'dashboard' : (module == 'S' ? 'spreadsheet' : 'view')}`),
		content: [
			{id: 'create', name: msg('Create')},
		]
	});
	if (cmd?.id == null) return null;

	const contextData = utils.mergeDeep(
		{version: '2', module, ...(isMultiContext(module) ? {items: []} : {})},
		defaults ?? {}
	) as ContextData|MultiContextData;

	const context = await rp.ms('analytics').post<ReportContext>('context', {
		name: cmd.value || createName(module),
		module: module,
		context: contextData,
	});

	await openReport(context.id_context, {
		context: context,
		target: 'new_tab',
		title: context.name,
	});

	return context;
}

export async function openContextJson(context: Partial<ReportContext>) {
	context = await getContext(context);
	rp.host.open(`analytics-contextDataEditor-${context.id_context}`);
}

export async function openContextSql(context: Partial<ReportContext>) {
	const sql = await rp.ms('analytics').post('reportQuery', context.context) as string;
	const id = `temp_${Math.random().toString(36).substring(7)}`;
	const editor = (await rp.host.open(`etl-sqlEditor-${id}`, {title: `query`})) as SqlEditor;
	editor.adapter.options = utils.mergeDeep(editor.adapter.options, {
		toolbar: {
			save: {enabled: false},
			discard: {enabled: false}
		},
		history: false
	});
	editor.editor.content = sql;
	return editor;
}

export async function deleteContext(context: Partial<ReportContext>) {
	const command = await rp.host.openMenu({
		content: 'yes_no',
		text: `Are you sure you want to delete '#${context.id_context} ${context.name}'?`
	});
	if (command?.id == 'Y') {
		return await rp.ms('analytics').delete(`context/${context.idz}`);
	}
}

export async function openContextFilter(idContext: number, itemIndex?: number) {
	rp.host.openSidebarComponent('analytics');
	const sidebar = (await rp.host.getComponent('analytics')) as Sidebar;
	sidebar.openFilter(idContext, itemIndex);
}

